import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { Field, Form } from 'react-final-form';
import { CubitTextFieldAdapter } from './cubit-inputs/react-form-adapters/cubit-text-field-adapter';
import { useState } from 'react';
import { CubitSelectAdapter } from './report-select-adapter';
import { useTranslation } from 'react-i18next';
import { ValidatorRequired } from '../utils/validators/form-validators';
import ReportConfigClient from '../clients/ReportConfigClient';
import { ReportsActions } from '../redux/reports-actions';
import { useDispatch, useSelector } from 'react-redux';
import pascalcase from '../utils/pascal-case';
import { AppState } from '../redux/app-store';
import { useHistory } from 'react-router-dom';
import { WhiteOutlinedButton } from './general/buttons/white-outlined-button';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

type Props = {
  sourceReportCode: string;
  sourceReportGroupCode?: string;
};

export const CopyReport: React.FC<Props> = ({
  sourceReportCode,
  sourceReportGroupCode,
}) => {
  const [isOpen, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const userConfig = useSelector(
    (state: AppState) => state.reports.userConfig
  );
  const reportGroups = useSelector(
    (state: AppState) => state.reports.reportGroupList
  );
  const editableGroups = (reportGroups || []).filter(
    (group) => group.isGroupEditAllowed
  );
  const selectedGroup = editableGroups.find(
    (group) => group.code === sourceReportGroupCode
  );

  const onSubmit = (values: any) => {
    const title = values.title?.trim();
    const groupCode = values.groupCode?.value || values.groupCode;
    if (!title || !groupCode || saving) {
      return;
    }

    setSaving(true);
    ReportConfigClient.copyReport({
      sourceReportCode,
      name: title,
      code: pascalcase(title),
      groupCode,
    }).subscribe({
      next: (result) => {
        setSaving(false);
        if (!result) {
          return;
        }
        ReportsActions.actionLoadReportGroups()(dispatch);
        setOpen(false);
        history.push(`/reports/${groupCode}/${result.code}`);
      },
      error: () => setSaving(false),
    });
  };

  if (!sourceReportCode || !userConfig?.isReportManagementAllowed) {
    return null;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', marginRight: '12px' }}>
      <WhiteOutlinedButton
        startIcon={<ContentCopyIcon />}
        text={t('COPY_REPORT', 'Lag kopi av rapport')}
        onClick={() => setOpen(true)}
      />

      <Dialog
        maxWidth={'xs'}
        fullWidth={true}
        open={isOpen}
        onClose={() => !saving && setOpen(false)}>
        <DialogTitle>
          {t('COPY_REPORT', 'Lag kopi av rapport')}
        </DialogTitle>

        <Form
          key={String(isOpen)}
          id='copy-report-form'
          onSubmit={onSubmit}
          initialValues={{
            ...(selectedGroup
              ? {
                  groupCode: {
                    label: selectedGroup.label,
                    value: selectedGroup.code,
                  },
                }
              : {}),
          }}
          render={({ handleSubmit, values }) => (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <div style={{ margin: '8px 0' }}>
                  <Field
                    width='100%'
                    name={'title'}
                    component={CubitTextFieldAdapter}
                    label={t('TITLE', 'Tittel')}
                    validate={(value) =>
                      !value?.trim()
                        ? t('TEXT_REQUIRED', 'Required')
                        : undefined
                    }
                    variant='standard'
                  />
                </div>
                <div style={{ margin: '8px 0' }}>
                  <Field
                    width='100%'
                    name={'groupCode'}
                    component={CubitSelectAdapter}
                    label={t('REPORT_GROUP', 'Rapportgruppe')}
                    validate={(value) =>
                      ValidatorRequired(value, t('TEXT_REQUIRED', 'Required'))
                    }
                    variant='standard'
                    options={editableGroups.map((g) => ({
                      label: g.label,
                      value: g.code,
                    }))}
                  />
                </div>
              </DialogContent>
              <DialogActions>
                <Button
                  variant={'text'}
                  disabled={saving}
                  onClick={() => setOpen(false)}>
                  {t('CANCEL', 'Avbryt')}
                </Button>
                <Button
                  variant={'contained'}
                  type='submit'
                  disabled={!values.title?.trim() || !values.groupCode || saving}>
                  {t('saveReport', 'Lagre rapport')}
                </Button>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>
    </div>
  );
};
